// crewbit-admin/src/pages/Settings/pages/AutoFeaturesPage.tsx
//
// Schedule Module Phase C7 — Settings → Auto Features (compound feature admin).
// Master toggles for auto-status / auto-text / auto-mileage, geofence radius
// slider, and auto-text template editor with live preview.
//
// Writes via update_compound_feature_settings SECDEF RPC.
// Spec: docs/sessions/SESSION_SCHEDULE_PHASE_C7_PROMPT.md §3.8.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { callScheduleRpc } from '../../../lib/scheduleRpc';
import { lookupErrorCopy } from '../../../lib/errorMap';
import { useAuthStore } from '../../../stores/authStore';
import { palette, admin } from '../../../styles/tokens';

interface CompoundSettings {
  auto_status_enabled: boolean;
  auto_text_enabled: boolean;
  auto_mileage_enabled: boolean;
  geofence_radius_m: number;
  auto_text_template: string | null;
}

const DEFAULT_TEMPLATE = 'Hi {client_name}, your tech {operator_name} has arrived.';
const RADIUS_MIN = 50;
const RADIUS_MAX = 500;
const RADIUS_STEP = 25;

export function AutoFeaturesPage() {
  const businessId = useAuthStore((s) => s.business?.id);
  const [settings, setSettings] = useState<CompoundSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('businesses')
      .select('auto_status_enabled, auto_text_enabled, auto_mileage_enabled, geofence_radius_m, auto_text_template')
      .eq('id', businessId)
      .maybeSingle();
    if (!error && data) {
      setSettings({
        auto_status_enabled: data.auto_status_enabled ?? false,
        auto_text_enabled: data.auto_text_enabled ?? false,
        auto_mileage_enabled: data.auto_mileage_enabled ?? false,
        geofence_radius_m: data.geofence_radius_m ?? 100,
        auto_text_template: data.auto_text_template ?? null,
      });
    }
    setLoading(false);
  }, [businessId]);

  useEffect(() => {
    document.title = 'CrewBit · Auto Features';
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial async fetch on mount; fetchSettings internally setLoading(false) then setSettings(...) — standard pattern for SELECT-on-mount, no cascading-render risk in practice
    void fetchSettings();
  }, [fetchSettings]);

  const save = useCallback(async (patch: Partial<CompoundSettings>) => {
    if (!settings) return;
    setErrorMsg(null);
    setSuccessMsg(null);
    setSaving(true);

    const merged = { ...settings, ...patch };
    setSettings(merged);

    const result = await callScheduleRpc('update_compound_feature_settings', {
      p_settings: {
        auto_status_enabled: merged.auto_status_enabled,
        auto_text_enabled: merged.auto_text_enabled,
        auto_mileage_enabled: merged.auto_mileage_enabled,
        geofence_radius_m: merged.geofence_radius_m,
        auto_text_template: merged.auto_text_template,
      },
    });

    setSaving(false);
    if (!result.success) {
      setErrorMsg(lookupErrorCopy('update_compound_feature_settings', result.error.code));
      await fetchSettings();
      return;
    }
    setSuccessMsg('Settings saved.');
    setTimeout(() => setSuccessMsg(null), 3000);
  }, [settings, fetchSettings]);

  if (loading || !settings) {
    return (
      <div style={{ padding: 24, maxWidth: 720 }}>
        <h1 style={h1Style}>Auto Features</h1>
        <p style={captionStyle}>Loading...</p>
      </div>
    );
  }

  const templateValue = settings.auto_text_template ?? DEFAULT_TEMPLATE;
  const previewText = templateValue
    .replace(/\{client_name\}/g, 'Sarah')
    .replace(/\{operator_name\}/g, 'Marcus')
    .replace(/\{business_name\}/g, 'Acme Services');

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <h1 style={h1Style}>Auto Features</h1>
      <p style={captionStyle}>
        Configure compound field-ops features. When operators enable background GPS
        on their device, these features fire automatically on job-site arrival.
      </p>

      {errorMsg && (
        <div style={errorBannerStyle}>{errorMsg}</div>
      )}
      {successMsg && (
        <div style={successBannerStyle}>{successMsg}</div>
      )}

      {/* ── Master toggles ── */}
      <SectionHeader title="Feature toggles" />

      <ToggleRow
        label="Auto-status advance"
        description="Automatically mark bookings as in-progress when operator arrives at the job site"
        checked={settings.auto_status_enabled}
        disabled={saving}
        onChange={(v) => save({ auto_status_enabled: v })}
      />
      <ToggleRow
        label="Auto-text client"
        description="Send an arrival notification to the client when operator arrives (email fallback; SMS coming soon)"
        checked={settings.auto_text_enabled}
        disabled={saving}
        onChange={(v) => save({ auto_text_enabled: v })}
      />
      <ToggleRow
        label="Auto-mileage capture"
        description="Capture GPS mileage between stops for T2125 tax reporting"
        checked={settings.auto_mileage_enabled}
        disabled={saving}
        onChange={(v) => save({ auto_mileage_enabled: v })}
      />

      {/* ── Geofence radius ── */}
      <SectionHeader title="Geofence radius" />
      <p style={{ ...captionStyle, marginBottom: 12 }}>
        How close operators must be to a job site for arrival detection to trigger.
        Smaller = more precise; larger = triggers earlier.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <input
          type="range"
          min={RADIUS_MIN}
          max={RADIUS_MAX}
          step={RADIUS_STEP}
          value={settings.geofence_radius_m}
          disabled={saving}
          onChange={(e) => setSettings({ ...settings, geofence_radius_m: Number(e.target.value) })}
          onMouseUp={() => save({ geofence_radius_m: settings.geofence_radius_m })}
          onTouchEnd={() => save({ geofence_radius_m: settings.geofence_radius_m })}
          style={{ flex: 1, accentColor: palette.accent }}
        />
        <span style={{
          fontSize: 14, fontWeight: 600, color: admin.header,
          minWidth: 60, textAlign: 'right',
        }}>
          {settings.geofence_radius_m}m
        </span>
      </div>

      {/* ── Auto-text template editor ── */}
      {settings.auto_text_enabled && (
        <>
          <SectionHeader title="Arrival message template" />
          <p style={{ ...captionStyle, marginBottom: 8 }}>
            Variables: <code>{'{client_name}'}</code>, <code>{'{operator_name}'}</code>, <code>{'{business_name}'}</code>
          </p>
          <textarea
            value={templateValue}
            disabled={saving}
            onChange={(e) => setSettings({
              ...settings,
              auto_text_template: e.target.value || null,
            })}
            onBlur={() => save({ auto_text_template: settings.auto_text_template })}
            rows={3}
            style={textareaStyle}
          />
          <div style={previewBoxStyle}>
            <div style={{ fontSize: 11, fontWeight: 600, color: admin.caption, marginBottom: 6, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
              Preview
            </div>
            <div style={{ fontSize: 14, color: admin.body, lineHeight: 1.5 }}>
              {previewText}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ──

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: 16, fontWeight: 600, color: admin.header,
      marginTop: 28, marginBottom: 12,
      paddingBottom: 8, borderBottom: `1px solid ${admin.border}`,
    }}>
      {title}
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function ToggleRow({ label, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '14px 0', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, width: 18, height: 18, accentColor: palette.accent }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: admin.header }}>{label}</div>
        <div style={{ fontSize: 13, color: admin.caption, marginTop: 2 }}>{description}</div>
      </div>
    </label>
  );
}

// ── Styles ──

const h1Style: React.CSSProperties = {
  fontSize: 22, fontWeight: 600, color: admin.header, marginBottom: 8,
};

const captionStyle: React.CSSProperties = {
  fontSize: 14, color: admin.caption, marginBottom: 24,
};

const errorBannerStyle: React.CSSProperties = {
  padding: 12, borderRadius: 8, backgroundColor: palette.redWash,
  color: palette.red, fontSize: 14, marginBottom: 16,
};

const successBannerStyle: React.CSSProperties = {
  padding: 12, borderRadius: 8, backgroundColor: palette.greenWash,
  color: palette.green, fontSize: 14, marginBottom: 16,
};

const textareaStyle: React.CSSProperties = {
  width: '100%', padding: 12, borderRadius: 8,
  border: `1px solid ${admin.border}`, fontSize: 14,
  fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical',
  boxSizing: 'border-box',
};

const previewBoxStyle: React.CSSProperties = {
  marginTop: 12, padding: 16, borderRadius: 8,
  backgroundColor: palette.g50, border: `1px solid ${admin.border}`,
  marginBottom: 24,
};

export default AutoFeaturesPage;
